export const MANUAL_MD = `# 📘 Manuale Verixa · Versione 1.0

> Guida completa all'uso di Verixa, la piattaforma di gestione HR multi-tenant. Aggiornato a Maggio 2026.

---

## 🚀 1. Primi passi

### 1.1 Cos'è Verixa
Verixa è una piattaforma SaaS per la gestione delle risorse umane: presenze QR con GPS, ferie e permessi, rimborsi spese, commesse, documenti firmati digitalmente, integrazione paghe e molto altro.

### 1.2 I 4 ruoli utente
| Ruolo | Cosa può fare |
|-------|----------------|
| **Super Admin** (di Verixa) | Gestisce tutte le aziende, attivazione/disattivazione, piani |
| **Admin azienda** | Gestisce la propria azienda (dipendenti, commesse, paghe, documenti) |
| **Sub-Amministratore** | Come admin + può generare QR di timbratura e inviare comunicazioni massive |
| **Dipendente** | Timbra presenze, richiede ferie/permessi, scarica cedolini, firma documenti |

### 1.3 Come accedere
1. Vai su https://calendar-expense-hub.emergent.host/login
2. Inserisci email e password
3. Se hai installato la PWA dal tuo telefono, ti aprirà direttamente la pagina di login

---

## 🏢 2. Per le aziende (Admin/Sub-Admin)

### 2.1 Configurazione iniziale
**Primo accesso → Anagrafica Azienda**: compila i dati fiscali completi (P.IVA, sede legale, IBAN, PEC, SDI). Senza questi, alcune funzioni (cedolini, fatture, paghe) non funzioneranno.

### 2.2 Aggiungere dipendenti
Hai 2 modi per aggiungerli:

**Modo A · Manuale** (\`Dipendenti → Nuovo dipendente\`)
- Inserisci nome, cognome, email, CF, mansione
- Scegli avatar e commesse assegnate
- Il dipendente riceve un'email per impostare la propria password

**Modo B · Auto-registrazione** (consigliato)
- Condividi il link \`/register-employee\` con i dipendenti
- Loro si registrano da soli indicando la P.IVA della tua azienda
- Tu approvi dalla sezione **Richieste iscrizione** (badge rosso in sidebar)
- Conforme GDPR (consensi espliciti, audit log)

### 2.3 Gestione dipendenti
- **Avatar**: clicca l'iniziale → scegli tra 13 stili
- **Toggle ON/OFF**: disattiva temporaneamente (maternità, aspettativa) senza eliminare l'account
- **Commesse**: assegna progetti multipli con il picker a chip
- **Aziende**: un dipendente può lavorare per più aziende (multi-tenant)

### 2.4 Promuovere a Sub-Amministratore
\`Sub-amministratori → Promuovi a sub-admin\`. Il sub-admin avrà permessi extra:
- ⚠️ **Solo i sub-admin possono generare QR di timbratura**
- ⚠️ **Solo i sub-admin possono inviare comunicazioni massive**

---

## 📍 3. Presenze QR

### 3.1 Generare un QR (sub-admin)
\`Presenze QR → Nuovo QR\`
- Indica indirizzo, città, CAP
- Imposta il raggio di validità (es. 50–100 m per uffici, 200 m per cantieri)
- Trascina il marker sulla mappa per le coordinate esatte
- Stampa il QR e affiggi all'ingresso

### 3.2 Timbrare (dipendente)
- **Entrata** → bottone arancione
- **Uscita** → bottone bianco
- **🍴 Pausa pranzo** → inizio pausa (timbratura ambra)
- **☕ Fine pausa** → fine pausa

> Se il GPS fallisce, puoi inserire manualmente le coordinate (sarà segnalato come timbratura "anomala" da approvare).

### 3.3 Trasferte
- Modalità trasferta sul check-in tracca il percorso GPS
- Termina automaticamente a fine mese (GDPR)

---

## 📅 4. Ferie, Permessi, Turni

### 4.1 Richiesta ferie (dipendente)
\`Portale → Ferie → Nuova richiesta\` → tipo (Ferie/Permesso/Malattia), date, ore, motivo.

### 4.2 Approvazione (admin)
\`Ferie/Permessi → richieste pending\` → Approva o Rifiuta con motivazione.

### 4.3 Turni
\`Turni → Nuovo turno\`: data, ora inizio/fine, ruolo, dipendenti assegnati. **Pubblica** rende visibile ai dipendenti.

---

## 💰 5. Rimborsi spese

### 5.1 Workflow
1. **Dipendente** carica scontrino (foto/PDF) + descrizione + importo
2. **Admin** approva o rifiuta dalla pagina Rimborsi
3. Gli approvati confluiscono automaticamente nell'export paghe

### 5.2 Categorie
Trasferta · Pasti · Carburante · Pernottamento · Materiali · Altro

---

## 📄 6. Documenti

### 6.1 Genera lettera (5 template AI)
- Comunicazione aziendale · Lettera missiva · Interruzione servizio · Piano ferie · **Rapporto assemblea sindacale**
- Compila i campi → genera con AI → invia per firma digitale

### 6.2 Firma digitale
Il dipendente firma con dito/penna sul canvas. Il sistema genera un **hash SHA-256** del documento + firma → integrità garantita per 10 anni.

### 6.3 Invio massivo (sub-admin)
\`Documenti → Invia a tutti i dipendenti\` (tasto nero in alto):
- **Tutti i dipendenti** o **singolo** (ricerca per nome/email)
- Il documento appare nella tab **Archivio Comunicazioni** per tutti

### 6.4 Archivio Comunicazioni
Tab visibile a **tutti** (admin + dipendenti). I dipendenti vedono solo le proprie.

---

## 💼 7. Commesse / Progetti

\`Commesse → Nuova commessa\`
- **Codice** (es. C-2026-001) · **Cliente** · **Scadenza**
- Vedi chip con conteggio dipendenti assegnati
- Click sul chip → elenco completo dipendenti collocati

---

## 📊 8. Cedolini e Paghe

### 8.1 Cedolini
\`Cedolini\` → upload PDF cedolino dipendente. Il dipendente lo trova nel suo portale.

### 8.2 Export Paghe (multi-formato)
\`Paghe\` → 5 tracciati disponibili:
- **CSV generico** (Excel)
- **Zucchetti Paghe Web**
- **TeamSystem (Gecom)**
- **Inaz Paghe**
- **PDF riepilogo** mensile

Aggrega automaticamente timbrature + ferie/permessi/malattia approvati.

---

## 🤖 9. AI Analisi
\`AI Analisi\` rileva pattern anomali (timbrature ripetute fuori orario, anomalie GPS, sequenze sospette). Powered da GPT-5.2.

---

## 📈 10. Statistiche Avanzate
\`Statistiche\` (in cima alla sidebar):
- 4 KPI in alto (Headcount, Documenti firmati 30gg, Rimborsi approvati, Anomalie totali)
- Grafico timbrature mensili (entrate, uscite, pause, anomalie)
- Top 5 dipendenti per timbrature
- Ferie aggregate per tipo

---

## 🔔 11. Notifiche
\`Notifiche\`: feed centralizzato. Click per segnare come letta. Tutte le comunicazioni inviate ai dipendenti generano automaticamente una notifica.

---

## 🛡️ 12. Sicurezza · Audit log
\`Audit log\` (per admin): chi ha fatto cosa, quando. Conservato 12 mesi. Filtro per email/azione. Eventi tracciati:
- Promote/demote sub-admin · Invito sub-admin
- Toggle dipendente · Approvazione richieste iscrizione
- Invio documenti massivi · Generazione/eliminazione QR

---

## 🔒 13. Privacy GDPR
\`Privacy GDPR\` (per tutti):
- **Esporta i miei dati** (art. 15/20 GDPR) → file JSON portatile
- **Cancella account** (art. 17 GDPR) → diritto all'oblio, con pseudonimizzazione record fiscali
- **DPA** (solo admin) → scarica template precompilato per art. 28

---

## 💳 14. Piani e fatturazione
\`Piani\`: confronta i 4 tier e attiva tramite Stripe. Trial 14 giorni gratuito. Enterprise = preventivo personalizzato.

---

## ❓ 15. FAQ

**D: Posso usare Verixa offline?**  
R: Sì. È una PWA con Service Worker. Installala dal browser (icona "Aggiungi alla home") e potrai consultare/timbrare anche senza connessione.

**D: Cosa succede se un dipendente è in maternità?**  
R: Disattivalo con il toggle ON/OFF e indica il motivo. Non occuperà più seat, ma i suoi dati rimangono. Riattivalo al rientro.

**D: Posso modificare le coordinate di un QR già stampato?**  
R: Sì, dal pannello \`Presenze QR\`. Il QR resta valido (è solo un identificativo), cambia solo la zona di validità GPS.

**D: Come fa un dipendente a installare la PWA?**  
R: Iphone → Safari → tasto Condividi → "Aggiungi alla schermata Home". Android → Chrome → menu → "Installa app".

**D: Verixa è conforme GDPR?**  
R: Sì. Consensi espliciti, registro trattamenti, DPA template, diritto all'oblio. Dati ospitati su MongoDB Atlas (UE).

---

## 🆘 16. Supporto
- 📧 Email: \`support@verixa.it\`
- 🛡️ Privacy: \`privacy@verixa.it\`
- 📜 Legale: \`legal@verixa.it\`
- 🎫 Apri un ticket dall'app: \`Ticket → Nuovo ticket\`

---

*Verixa © 2026 — Tutti i diritti riservati.*`;
